import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { PATH_HOME } from '../../routes/paths';
import { sendToSentry } from '../../utils/owner-portal/sentryReporter';
import { CenteredStack } from '../containers/CenteredStack';
import { Button } from '../ui/Button';

interface StandardErrorFallbackProps {
  errorInfo: { error?: Error | { name?: string; message?: string } } | null;
  customErrorMessage: string;
  reportToSentry: boolean;
  fingerprintDetail?: string;
  fingerprintScope?: string;
}

export function StandardErrorFallback({
  errorInfo,
  customErrorMessage,
  reportToSentry,
  fingerprintDetail,
  fingerprintScope
}: StandardErrorFallbackProps) {
  const [reported, setReported] = useState(false);

  //NOTE: ChunkLoadError happens when a browser is looking for an outdated chunk. This is due
  //      to a version change, possibly in conjunction with a stale session and certain browser
  //      cache behavior. I didn't investigate too deeply, because no matter what, our solution
  //      is to reload the page.
  //      There is a much more involved technique before to abstract a lazy-loading manager.
  //      It looks interesting, but it's major overkill for the current problem.
  //
  // https://sentry.io/answers/chunk-load-errors-javascript/
  // https://medium.com/@botfather/react-loading-chunk-failed-error-88d0bb75b406
  if (errorInfo && errorInfo.error) {
    let chunkErrorDetected = false;

    if (errorInfo.error.name === 'ChunkLoadError') {
      console.log('ChunkLoadError detected via name');
      chunkErrorDetected = true;
    } else if (
      typeof errorInfo.error.message === 'string' &&
      errorInfo.error.message.indexOf('Loading chunk') === 0 &&
      errorInfo.error.message.indexOf('failed') !== -1
    ) {
      console.log('ChunkLoadError detected via message');
      chunkErrorDetected = true;
    }

    if (chunkErrorDetected) {
      console.log('Reloading page for new version...');
      window.location.reload();
      return null;
    }

    // process this as a normal error
  }

  if (reportToSentry && !reported) {
    setReported(true);

    console.log('Reporting to Sentry', errorInfo);

    sendToSentry(
      errorInfo?.error as Error,
      null,
      fingerprintScope ? fingerprintScope : 'runtime error',
      fingerprintDetail ? fingerprintDetail : 'none'
    );
  }

  return (
    <CenteredStack>
      <div className="animate-in fade-in slide-in-from-bottom-5 duration-500">
        <h5 className="mb-4 text-center text-xl font-semibold">{customErrorMessage}</h5>
      </div>

      <div className="animate-in fade-in zoom-in-95 duration-500 delay-100 my-10 sm:my-20">
        <AlertTriangle className="h-32 w-32 text-destructive" />
      </div>

      <Button asChild variant="ghost">
        <RouterLink to={PATH_HOME.root}>Home</RouterLink>
      </Button>
    </CenteredStack>
  );
}
